/**
 * Other entity models — ES module versions of types from:
 *   OtherEntities.ts, CCPerson.ts, CCVehicle.ts, CallAlert.ts,
 *   EntityBase.ts, DataObjectLocationFields.ts, NPSDRelationships.ts,
 *   LegacyNocInfo.ts
 */

import type {
  AttachmentData,
  AttachmentInfoData,
  EmailData,
  PhoneNumberData,
  WarningData,
  GateCodeData,
  ConnectionsData,
  CCPersonData,
  CCVehicleData,
  CompanyData,
  CallCardClientGroupSiteData,
  CallCardClientGroupData,
  CallCommentData,
} from '@/services/api-types';

export type {
  AttachmentData,
  AttachmentInfoData,
  EmailData,
  PhoneNumberData,
  WarningData,
  GateCodeData,
  ConnectionsData,
  CCPersonData,
  CCVehicleData,
  CompanyData,
  CallCardClientGroupSiteData,
  CallCardClientGroupData,
  CallCommentData,
};

// ── Report sub-entities (from OtherEntities.ts) ──────────────────────

export interface BackUpUnitData {
  id: number | null;
  officer: number | null;
  officerName: string;
  dispatched: string | null;
  enroute: string | null;
  onScene: string | null;
  cleared: string | null;
}

export interface RespondingAgencyData {
  id: number | null;
  agency: string;
  people: string;
  arrived: string | null;
  notes: string;
}

export interface OffenderData {
  id: number | null;
  npsdPerson: number | null;
  arrested: number | null;
  offenses: number[] | null;
  officerNotes: string;
  personName: string;
  tempid: string;
}

export interface AccidentData {
  accidentPersonList: AccidentPersonData[];
  roadwayConditions: string;
  findings: string;
  notes: string;
}

export interface AccidentPersonData {
  npsdPerson: number | null;
  personName: string | null;
  npsdVehicle: number | null;
  vehicleName: string | null;
  isAtFault: number | null;
  isInjured: number | null;
  isVehicleDamaged: number | null;
  injuryNotes: string;
  id: number | null;
  tempid: string;
}

export interface PropertyData {
  id: number | null;
  item: string;
  npsdPersonOwner: number | null;
  npsdBusinessOwner: number | null;
  npsdClientOwner: number | null;
  reportedAs: number | null;
  estimatedValue: EstimatedValueData;
  damages: string;
  storageLoc: string;
  brand: string;
  model: string;
  year: string;
  colors: string;
  serialNum: string;
  location: string;
  officerNotes: string;
  tempid: string;
}

export interface EstimatedValueData {
  code: string | null;
  value: number | null;
}

export interface MedicalReportData {
  id: number | null;
  patient: number | null;
  hospitalized: number | null;
  symptomsOrInjuriesTxt: string;
  officerNotesTxt: string;
  heartProblems: number | null;
  highBloodPressure: number | null;
  hypertensionPatientHistory: number | null;
  diabetes: number | null;
  medications: number | null;
  medicationsTxt: string;
  otcMedications: number | null;
  otcMedicationsTxt: string;
  vitamins: number | null;
  vitaminsTxt: string;
  allergicToMedications: number | null;
  allergicToMedicationsTxt: string;
  lastAte: any;
  majorSurgeries: number | null;
  majorSurgeriesTxt: string;
  abdominalPain: number | null;
  nausea: number | null;
  bloating: number | null;
  heartburn: number | null;
  ulcer: number | null;
  difficultySwallowing: number | null;
  jaundice: number | null;
  liverDisease: number | null;
  gallBladderProblems: number | null;
  pancreatitis: number | null;
  bowelHabits: number | null;
  bloodyStool: number | null;
  colonCancer: number | null;
  hemorrhoids: number | null;
  abdominalTxt: string;
  chestPain: number | null;
  palpitations: number | null;
  swollenLegs: number | null;
  hypertensionCardio: number | null;
  hyperlipidemia: number | null;
  angina: number | null;
  rheumaticFever: number | null;
  claudication: number | null;
  bloodClots: number | null;
  varicoseVeins: number | null;
  cardiovascularTxt: string;
  urinaryProblems: number | null;
  bloodInUrine: number | null;
  kidneyStones: number | null;
  urinaryInfections: number | null;
  sexualDysfunction: number | null;
  useContraceptives: number | null;
  urinaryTxt: string;
  personName: string;
  tempid: string;
}

export interface StatementData {
  id: number | null;
  npsdPerson: number | null;
  statement: string;
  personName: string;
  personRoles: string[] | null;
  tempid: string;
}

export interface DigitalEvidenceData {
  id: number | null;
  title: string;
  evidenceOf: number | null;
  involving: string;
  evidenceOfName: string;
  involvingName: string;
  attachmentList: AttachmentData[];
  attachmentInfoList: AttachmentInfoData[];
  tempid: string;
}

export interface NameValueData {
  id: number | null;
  name: string;
  value: string;
}

export interface NPSDReportData {
  id: number;
  callDate: string | null;
  natureOfCall: number[];
  roles: number[];
  offenses: number[];
}

// ── NPSD Connection types (from NPSDRelationships.ts) ────────────────

export interface NpsdPersonConnectionData {
  id: number | null;
  relationship: number | null;
  notes: string | null;
  firstName: string | null;
  lastName: string | null;
  dob: string | null;
}

export interface NpsdVehicleConnectionData {
  id: number | null;
  plateNumber: string | null;
  plateState: string | null;
  make: string | null;
  model: string | null;
  color: string | null;
  vin: string | null;
}

export interface NpsdBusinessConnectionData {
  id: number | null;
  businessName: string | null;
  branchName: string | null;
  website: string | null;
  roles: string | null;
}

export interface NpsdCGConnectionData {
  id: number | null;
  name: string | null;
  locCity: string | null;
  locState: string | null;
  website: string | null;
  roles: string | null;
}

export enum RelType {
  person = 0,
  vehicle = 1,
  business = 2,
  client = 3,
}

export enum RelTypePlural {
  people = 0,
  vehicles = 1,
  businesses = 2,
  clients = 3,
}

// ── Navigation types (from navigation-type.ts) ──────────────────────

//"NavigationType" is a reserved class.
export enum OrnaNavigationType {
  GOOGLE_MAPS = 1,
  WAZE = 2,
  IN_APP = 3,
}

// ── Custom Location types (from custom-location-or-coordinates.ts) ──

export interface CustomLocationOrCoordinates {
  id?: number;
  type?: CustomLocationOrCoordinatesType;
  location?: string;
  latitude?: number;
  longitude?: number;
}

export enum CustomLocationOrCoordinatesType {
  CUSTOM_LOCATION,
  COORDINATES,
}

// ── Call Alert (from CallAlert.ts) ───────────────────────────────────

export interface CallAlertData {
  id: number | null;
  locFormattedAddress: string | null;
  callAlertTypeId: number | null;
  alertStartTime: string | null;
  alertEndTime: string | null;
  message: string | null;
  latitude: number | null;
  longitude: number | null;
  customLocationId: boolean | null;
}

// ── DataObjectLocationFields (from DataObjectLocationFields.ts) ──────

export interface DataObjectLocationFieldsType {
  address: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    formattedAddress: string;
  };
  coordinates: {
    latitude: string;
    longitude: string;
  };
  customlocation: {
    customLocationId: string;
  };
  properties: {
    isOnlySavingCoordinatesOnCustomLocation: boolean;
  };
}

const callForServiceAndCaseReport: DataObjectLocationFieldsType = {
  address: { address: 'callLocAddress', city: 'callLocCity', state: 'callLocState', zip: 'callLocZip', country: 'callLocCountry', formattedAddress: 'callLocFormattedAddress' },
  coordinates: { latitude: 'latitude', longitude: 'longitude' },
  customlocation: { customLocationId: 'customLocationId' },
  properties: { isOnlySavingCoordinatesOnCustomLocation: false },
};

const npsdData: DataObjectLocationFieldsType = {
  address: { address: 'locAddress', city: 'locCity', state: 'locState', zip: 'locZip', country: 'locCountry', formattedAddress: 'locFormattedAddress' },
  coordinates: { latitude: 'latitude', longitude: 'longitude' },
  customlocation: { customLocationId: 'customLocationId' },
  properties: { isOnlySavingCoordinatesOnCustomLocation: true },
};

export const dataObjectLocationFields = {
  callForService: callForServiceAndCaseReport,
  caseReport: callForServiceAndCaseReport,
  business: npsdData,
  client: npsdData,
  person: npsdData,
  tourStop: {
    address: { formattedAddress: 'location' },
    coordinates: { latitude: 'locationLatitude', longitude: 'locationLongitude' },
    customlocation: { customLocationId: 'customLocationId' },
    properties: { isOnlySavingCoordinatesOnCustomLocation: false },
  } as DataObjectLocationFieldsType,
  callAlert: {
    address: { formattedAddress: 'locFormattedAddress' },
    coordinates: { latitude: 'latitude', longitude: 'longitude' },
    customlocation: { customLocationId: 'customLocationId' },
    properties: { isOnlySavingCoordinatesOnCustomLocation: true },
  } as DataObjectLocationFieldsType,
};

// ── Legacy NOC data (from LegacyNocInfo.ts) ──────────────────────────

export interface LegacyNocItem {
  code: number;
  value: string;
}

export const legacyNocs: LegacyNocItem[] = [
  { code: 1, value: '15 Minute Break' },
  { code: 2, value: 'Administration' },
  { code: 3, value: 'Assist Housekeeping' },
  { code: 4, value: 'Car Wash' },
  { code: 5, value: 'Internal Investigation' },
  { code: 6, value: 'Investigation' },
  { code: 7, value: 'Lunch/Meal Break' },
  { code: 8, value: 'Meeting' },
  { code: 9, value: 'Administration - Other' },
  { code: 10, value: 'Out Of Service - Gas/Fuel' },
  { code: 11, value: 'Patrol Watch List' },
  { code: 12, value: 'Relieve Officer For 15 Minute Break' },
  { code: 13, value: 'Relieve Officer For Lunch/Meal' },
  { code: 14, value: 'Shift Briefing' },
  { code: 15, value: 'Traffic Control' },
  { code: 16, value: 'Out At Office' },
  { code: 17, value: 'Out At Station' },
  { code: 18, value: 'Training - Administration' },
  { code: 19, value: 'Training - Batton & Handcuffs' },
  { code: 20, value: 'Training - CAD' },
  { code: 21, value: 'Training - Community Emergency Response Team' },
  { code: 22, value: 'Training - CPR' },
  { code: 23, value: 'Training - Dealing With Mentally Disturbed Persons' },
  { code: 24, value: 'Training - EMR' },
  { code: 25, value: 'Training - Firearms Training' },
  { code: 26, value: 'Training - First Aid' },
  { code: 27, value: 'Training - General Training' },
  { code: 28, value: 'Training - MDT' },
  { code: 29, value: 'Training - On The Job Training' },
  { code: 30, value: 'Training - Other' },
  { code: 31, value: 'Training - Radar Enforcement' },
  { code: 32, value: 'Training - Radio Communications' },
  { code: 33, value: 'Training - Report Writing' },
  { code: 34, value: 'Training - Scheduling' },
  { code: 35, value: 'Training - Traffic Stops' },
  { code: 36, value: 'Motor Vehicle Accident - With Gun Shot Wound' },
  { code: 37, value: 'Motor Vehicle Accident - Fatality' },
  { code: 38, value: 'Motor Vehicle Accident - Hit and Run - With Injuries' },
  { code: 39, value: 'Motor Vehicle Accident - Involving Motorcycle' },
  { code: 40, value: 'Motor Vehicle Accident - Involving Pedestrian' },
  { code: 41, value: 'Motor Vehicle Accident - Other (Emergency)' },
  { code: 42, value: 'Motor Vehicle Accident - With Injuries' },
  { code: 43, value: 'Building Alarm' },
  { code: 44, value: 'Burglary Alarm' },
  { code: 45, value: 'Commercial Alarm' },
  { code: 46, value: 'Fire Alarm' },
  { code: 47, value: 'Fire Sprinkler Alarm' },
  { code: 48, value: 'Intrusion Alarm' },
  { code: 49, value: 'Low Flow Alarm' },
  { code: 50, value: 'Motion Alarm' },
  { code: 51, value: 'Other Alarm' },
  { code: 52, value: 'Panic Alarm' },
  { code: 53, value: 'Perimeter Alarm' },
  { code: 54, value: 'Unknown Alarm' },
  { code: 55, value: 'Vehicle Alarm' },
  { code: 56, value: 'Apartment Fire' },
  { code: 57, value: 'Debris Fire' },
  { code: 58, value: 'House Fire' },
  { code: 59, value: 'Fire - Illegal Burning' },
  { code: 60, value: 'Mulch Fire' },
  { code: 61, value: 'Fire - Other' },
  { code: 62, value: 'Structure Fire' },
  { code: 63, value: 'Vehicle Fire' },
  { code: 64, value: 'Hazardous  Chemicals' },
  { code: 65, value: 'Down Power Lines' },
  { code: 66, value: 'Hazardous  Electrical' },
  { code: 67, value: 'Explosion' },
  { code: 68, value: 'Fire' },
  { code: 69, value: 'Gas Leak' },
  { code: 70, value: 'Hazardous - Other' },
  { code: 71, value: 'Sewer Leak' },
  { code: 72, value: 'Transformer Blown' },
  { code: 73, value: 'Water Leak' },
  { code: 74, value: 'Medical - Chest Pains' },
  { code: 75, value: 'Dead Body Found' },
  { code: 76, value: 'Medical - Fall Injury' },
  { code: 77, value: 'Medical - Heart Attack' },
  { code: 78, value: 'Medical - Heat Stroke' },
  { code: 79, value: 'Medical - Laceration' },
  { code: 80, value: 'Mental Health Crisis' },
  { code: 81, value: 'Medical - Other' },
  { code: 82, value: 'Medical - Person Down' },
  { code: 83, value: 'Medical - Shortness Of Breath' },
  { code: 84, value: 'Medical - Sick Or Injured Person' },
  { code: 85, value: 'Stabbing, Cutting Victim' },
  { code: 86, value: 'Suicide' },
  { code: 87, value: 'Trouble Breathing' },
  { code: 88, value: 'Assault With A Vehicle' },
  { code: 89, value: 'DWI / DUI' },
  { code: 90, value: 'Public Danger - Other (Driving)' },
  { code: 91, value: 'Reckless Driving' },
  { code: 92, value: 'Wrong Way Driver' },
  { code: 93, value: 'Assault (No Weapon)' },
  { code: 94, value: 'Fight In Progress' },
  { code: 95, value: 'Homicide' },
  { code: 96, value: 'Kidnapping' },
  { code: 97, value: 'Public Danger - Other' },
  { code: 98, value: 'Rape' },
  { code: 99, value: 'Sexual Assault (excl. Rape)' },
  { code: 100, value: 'Threat (No Weapon)' },
  { code: 101, value: 'Unknown Trouble' },
  { code: 102, value: 'Active Shooter' },
  { code: 103, value: 'Armed Robbery' },
  { code: 104, value: 'Assault (Weapon)' },
  { code: 105, value: 'Assault With Deadly Weapon' },
  { code: 106, value: 'Bomb Threat' },
  { code: 107, value: 'Drive By Shooting' },
  { code: 108, value: 'Explosive Device / Bomb' },
  { code: 109, value: 'Gun (Subject With Gun)' },
  { code: 110, value: 'Knife (Subject With Knife)' },
  { code: 111, value: 'Officer Involved In Shooting' },
  { code: 112, value: 'Other (Weapon)' },
  { code: 113, value: 'Shots Fired' },
  { code: 114, value: 'Threat with a Weapon' },
  { code: 115, value: 'Burglary In Progress' },
  { code: 116, value: 'Robbery / Burglary - Other' },
  { code: 117, value: 'Robbery In Progress' },
  { code: 118, value: 'Robbery' },
  { code: 119, value: 'Abduction / Kidnapping' },
  { code: 120, value: 'Air-Sea Rescue' },
  { code: 121, value: 'Combat Search and Rescue' },
  { code: 122, value: 'Ground Search and Rescue' },
  { code: 123, value: 'Missing Person' },
  { code: 124, value: 'Mountain Rescue' },
  { code: 125, value: 'Search and Rescue (Other)' },
  { code: 126, value: 'Urban Search and Rescue' },
  { code: 127, value: 'Water Rescue' },
  { code: 128, value: 'Aggressive Animal' },
  { code: 129, value: 'Animal At Large' },
  { code: 130, value: 'Cat Pickup' },
  { code: 131, value: 'Dead Animal In Road (Other)' },
  { code: 132, value: 'Dead Cat In Road' },
  { code: 133, value: 'Dead Dog In Road' },
  { code: 134, value: 'Dog Off Leash' },
  { code: 135, value: 'Animal - Other' },
  { code: 136, value: 'Animal - Pickup' },
  { code: 137, value: 'Animal Removal - Armadillos' },
  { code: 138, value: 'Animal Removal - Bird' },
  { code: 139, value: 'Animal Removal - Coyote' },
  { code: 140, value: 'Animal Removal - Deer' },
  { code: 141, value: 'Animal Removal - Fox' },
  { code: 142, value: 'Animal Removal - Opossum' },
  { code: 143, value: 'Animal Removal - Other' },
  { code: 144, value: 'Animal Removal - Raccoons' },
  { code: 145, value: 'Animal Removal - Rats' },
  { code: 146, value: 'Animal Removal - Skunk' },
  { code: 147, value: 'Animal Removal - Snake' },
  { code: 148, value: 'Follow Up - Fire Investigation' },
  { code: 149, value: 'Follow Up - Incident' },
  { code: 150, value: 'Follow Up - Other' },
  { code: 151, value: 'Follow Up - Police Investigation' },
  { code: 152, value: 'Follow Up - Security Investigation' },
  { code: 153, value: 'Follow Up - See The Party' },
  { code: 154, value: 'Internal Investigation - Loss Prevention Officer Related' },
  { code: 155, value: 'Internal Investigation - Loss Prevention Service Related' },
  { code: 156, value: 'Internal Investigation - Other' },
  { code: 157, value: 'Internal Investigation - Public Safety Officer Conduct' },
  { code: 158, value: 'Internal Investigation - Public Safety Service Related' },
  { code: 159, value: 'Internal Investigation - Security Guard Service Related' },
  { code: 160, value: 'Internal Investigation - Security Officer Conduct' },
  { code: 161, value: 'Found Property' },
  { code: 162, value: 'Lost Property Report' },
  { code: 163, value: 'Maintenance - General Repairs' },
  { code: 164, value: 'Maintenance - CCTV' },
  { code: 165, value: 'Maintenance - Other' },
  { code: 166, value: 'Maintenance - Remote Camera' },
  { code: 167, value: 'Maintenance - Vehicle' },
  { code: 168, value: 'Maintenance - Vehicle Camera' },
  { code: 169, value: 'Motorist Assist - Jump Start' },
  { code: 170, value: 'Motorist Assist - Out of Gas' },
  { code: 171, value: 'Motorist Assist - Traffic Control' },
  { code: 172, value: 'Motorist Assist - Change Tire' },
  { code: 173, value: 'Motorist Assist - Fuel' },
  { code: 174, value: 'Motorist Assist - Other' },
  { code: 175, value: 'Motorist Assist - Unknown Issue' },
  { code: 176, value: 'General Service - Other' },
  { code: 177, value: 'Assist Another Agency' },
  { code: 178, value: 'Check Solicitor' },
  { code: 179, value: 'Information Needed' },
  { code: 180, value: 'No Code' },
  { code: 181, value: 'Protective Detail' },
  { code: 182, value: 'Requested Patrol Of Area' },
  { code: 183, value: 'Special Assignment' },
  { code: 184, value: 'Special Event' },
  { code: 185, value: 'Surveillance Detail' },
  { code: 186, value: 'Security - Check' },
  { code: 187, value: 'Security - Post' },
  { code: 188, value: 'Welfare Check -  Open Door (Business)' },
  { code: 189, value: 'Welfare Check - Child Neglect' },
  { code: 190, value: 'Welfare Check - Children' },
  { code: 191, value: 'Welfare Check - Husband' },
  { code: 192, value: 'Welfare Check - On Officer' },
  { code: 193, value: 'Welfare Check - Open Door (Residence)' },
  { code: 194, value: 'Welfare Check - Other' },
  { code: 195, value: 'Welfare Check - Unsecured Building (Business)' },
  { code: 196, value: 'Welfare Check - Unsecured Building (Residence)' },
  { code: 197, value: 'Welfare Check - Wife' },
  { code: 198, value: 'Motor Vehicle Accident - Hit and Run - No Injuries' },
  { code: 199, value: 'Motor Vehicle Accident - No Injuries' },
  { code: 200, value: 'Motor Vehicle Accident - Other (Non-Emergency)' },
  { code: 201, value: 'Burglary' },
  { code: 202, value: 'Burglary / Theft - Other' },
  { code: 203, value: 'Shoplifting' },
  { code: 204, value: 'Stolen Vehicle' },
  { code: 205, value: 'Stolen Vehicle Recovery' },
  { code: 206, value: 'Theft' },
  { code: 207, value: 'Bogus Check' },
  { code: 208, value: 'Civil Matter' },
  { code: 209, value: 'Curfew / Loitering / Vagrancy' },
  { code: 210, value: 'Disorderly Conduct' },
  { code: 211, value: 'Domestic Violence' },
  { code: 212, value: 'Domestic Violence - Fight' },
  { code: 213, value: 'Drunk / Intoxicated' },
  { code: 214, value: 'Drunks Disturbing' },
  { code: 215, value: 'Forgery' },
  { code: 216, value: 'Gambling' },
  { code: 217, value: 'Harassment / Abuse' },
  { code: 218, value: 'Indecent Exposure' },
  { code: 219, value: 'Invasion of Privacy' },
  { code: 220, value: 'Juveniles Disturbing' },
  { code: 221, value: 'Littering' },
  { code: 222, value: 'Miscellaneous Animal Incident' },
  { code: 223, value: 'Molest' },
  { code: 224, value: 'Neighbor Trouble' },
  { code: 225, value: 'Noise, Loud Music Disturbing' },
  { code: 226, value: 'Obscene Communications' },
  { code: 227, value: 'Maintaining Order - Other' },
  { code: 228, value: 'Prostitution' },
  { code: 229, value: 'Riot' },
  { code: 230, value: 'Traffic Congestion' },
  { code: 231, value: 'Traffic Control Defect' },
  { code: 232, value: 'Traffic Hazard' },
  { code: 233, value: 'Trespassing' },
  { code: 234, value: 'Property Damage - Criminal' },
  { code: 235, value: 'Property Damage - Equipment' },
  { code: 236, value: 'Property Damage - Irrigation' },
  { code: 237, value: 'Property Damage - Landscape' },
  { code: 238, value: 'Property Damage - Lighting' },
  { code: 239, value: 'Property Damage - Monument' },
  { code: 240, value: 'Property Damage - Other / Unknown' },
  { code: 241, value: 'Property Damage - Right of Way' },
  { code: 242, value: 'Property Damage - Signage' },
  { code: 243, value: 'Property Damage - Stop Sign' },
  { code: 244, value: 'Property Damage - Street' },
  { code: 245, value: 'Property Damage - Street Sign' },
  { code: 246, value: 'Property Damage - Utility Fixture' },
  { code: 247, value: 'Suspicious Activity' },
  { code: 248, value: 'Suspicious Activity Inside Business' },
  { code: 249, value: 'Suspicious Activity Inside Home' },
  { code: 250, value: 'Suspicious - Other' },
  { code: 251, value: 'Suspicious Person' },
  { code: 252, value: 'Suspicious Prowler' },
  { code: 253, value: 'Suspicious Vehicle' },
  { code: 254, value: 'Abandoned Vehicle' },
  { code: 255, value: 'Check For Drugs' },
  { code: 256, value: 'Check For Glue Sniffing' },
  { code: 257, value: 'Check For Marijuana' },
  { code: 258, value: 'City Code Violation' },
  { code: 259, value: 'Fish and Game Law' },
  { code: 260, value: 'Illegal Parking' },
  { code: 261, value: 'Minor In Possession' },
  { code: 262, value: 'Violation - Other (Non-Emergency)' },
  { code: 263, value: 'Parole Violation' },
  { code: 264, value: 'Sell to Minor' },
  { code: 265, value: 'Speeding Cars (Drag Racing)' },
  { code: 266, value: 'Traffic Stop' },
  { code: 267, value: 'Robbery - Home Invasion' },
  { code: 268, value: 'Animal Disturbing, Barking Dog' },
  { code: 269, value: 'Disturbing Party' },
  { code: 270, value: 'Landlord - Tenant Dispute' },
  { code: 271, value: 'Theft from Vehicle' },
  { code: 272, value: 'Stolen Bicycle' },
  { code: 273, value: 'Stolen Property' },
  { code: 274, value: 'Recovered Bicycle' },
  { code: 275, value: 'Recovered Property' },
  { code: 276, value: 'Public Assist' },
  { code: 277, value: 'Motor Vehicle Accident - ATV/UTV' },
  { code: 278, value: 'Welfare Check -  Pedestrian' },
  { code: 279, value: 'Parking violation' },
  { code: 280, value: 'Unlawful Discharge of Firearm' },
  { code: 281, value: 'Vehicle Impound' },
  { code: 282, value: 'Vehicle Recovery / Tow' },
  { code: 283, value: 'Assist Sheriffs Office' },
  { code: 284, value: 'Assist Fire Department' },
  { code: 285, value: 'Assist Ambulance' },
  { code: 286, value: 'Assist State Troopers' },
  { code: 287, value: 'Assist Border Patrol' },
  { code: 288, value: 'Assist Game Warden' },
  { code: 289, value: 'Assist Other Agency' },
  { code: 290, value: 'Motor Vehicle Accident - RATV' },
  { code: 291, value: 'Battery' },
  { code: 292, value: 'Medical  - Trauma' },
  { code: 293, value: 'Accident Non-Injury Emergency' },
  { code: 294, value: 'Unlawful Dumping' },
  { code: 295, value: 'Property Check' },
  { code: 296, value: 'OHV Registration' },
  { code: 297, value: 'Training - ATV Safety Course' },
  { code: 298, value: 'Theft from Motor Vehicle' },
  { code: 299, value: 'Vandalism - Property' },
  { code: 300, value: 'Vandalism - Vehicle' },
  { code: 301, value: 'Accident Non-Injury' },
  { code: 302, value: 'Accident With Injury' },
  { code: 303, value: 'Motorist Assist - Vehicle Stuck' },
  { code: 304, value: 'Motorist Assist - Vehicle Disabled' },
  { code: 305, value: 'Motorist Assist - Vehicle Tow' },
  { code: 306, value: 'Funeral Escort' },
  { code: 307, value: 'Vehicle Repossession' },
  { code: 308, value: 'Pursuit' },
  { code: 309, value: 'Speeding' },
  { code: 310, value: 'Failure To Appear' },
  { code: 311, value: 'Misdemeanor Warrant' },
  { code: 312, value: 'Bail Revoke' },
  { code: 313, value: 'Felony Warrant' },
  { code: 314, value: 'Stolen License Plates' },
  { code: 315, value: 'Fireworks' },
  { code: 316, value: 'Vehicle Blocking' },
  { code: 317, value: 'Safety Hazard' },
  { code: 318, value: 'Illegal Dumping' },
  { code: 319, value: 'Parking Issue' },
  { code: 320, value: 'Unauthorized Parking' },
  { code: 321, value: 'Escort Detail' },
  { code: 322, value: 'Unsupervised Children' },
  { code: 323, value: 'Eviction Notice' },
  { code: 324, value: 'Maintenance Assist' },
  { code: 325, value: 'Odor of Marijuana' },
  { code: 326, value: 'Public Intoxication' },
  { code: 327, value: 'Serving Notices / Papers' },
  { code: 328, value: 'Unoccupied Vehicle' },
  { code: 329, value: 'Resident Assist' },
  { code: 330, value: 'ATM Escorts' },
  { code: 331, value: 'Medical - Stroke / CVA' },
  { code: 332, value: 'Domestic Dispute' },
  { code: 333, value: 'Possession Of A Weapon' },
  { code: 334, value: 'Possession Of Drugs' },
  { code: 335, value: 'Fugitive Of Justice' },
  { code: 336, value: 'Bond Skip' },
  { code: 337, value: 'Detained' },
  { code: 338, value: 'Local Assistance' },
  { code: 339, value: 'Possession Of Identity Or Financial Device' },
  { code: 340, value: 'Door Breached' },
  { code: 341, value: 'Flash Bang / Smoke Used' },
  { code: 342, value: 'Taser Discharged' },
  { code: 343, value: 'OC Spray Used' },
  { code: 344, value: 'OC Grenade / Tear Gas Used' },
  { code: 345, value: 'Transport Tracking' },
  { code: 346, value: 'Bail Bonds Warrant' },
  { code: 347, value: 'Bail Bonds Violation' },
  { code: 348, value: 'GPS Install' },
  { code: 349, value: 'GPS maintenance' },
  { code: 350, value: 'Arson' },
  { code: 351, value: 'Arson Investigation' },
  { code: 352, value: 'Motel Ejection' },
  { code: 353, value: 'Hotel Ejection' },
  { code: 354, value: 'Arrest Made' },
  { code: 355, value: 'Field Interview' },
  { code: 356, value: 'For Information Only' },
  { code: 357, value: 'PR Visit' },
  { code: 358, value: 'Refusal To Leave' },
  { code: 359, value: 'Maintenance Issue' },
  { code: 360, value: 'Panhandling' },
  { code: 361, value: 'Hot Shot - Human Waste' },
  { code: 362, value: 'Hot Shot - Syringe' },
  { code: 363, value: 'Elevator Rescue' },
  { code: 364, value: 'Lights Out' },
  { code: 365, value: 'Property Lights Check' },
  { code: 366, value: 'Homeless / Transit' },
  { code: 367, value: 'Gang Member' },
  { code: 368, value: 'Expired Tags' },
  { code: 369, value: 'In Route Backup' },
  { code: 370, value: 'Immediate Assistance' },
  { code: 371, value: 'Tow Request' },
  { code: 372, value: 'Tow In Progress' },
  { code: 373, value: 'Tow In Progress' },
  { code: 374, value: 'Vehicle Inspection/PMC' },
  { code: 375, value: 'Noise Disturbance' },
  { code: 376, value: 'Transient/Individual Removal' },
];

export const legacyNocCodeToValueMap: Record<number, string> = legacyNocs.reduce(
  (map, obj) => { map[obj.code] = obj.value; return map; },
  {} as Record<number, string>,
);
